<template>
  <section class="page">
    <div class="page__header">
      <h2 class="page__title">{{ $t('kps.inspections.title') }}</h2>
    </div>

    <div class="section-card">
      <DataTable :columns="visibleColumns" :rows="rows" :rowKey="rowKey" :headerHeight="56" :rowHeight="64">
        <template #head-is_completed>
          <div class="header-left">{{ $t('kps.inspections.colFulfillment') }}</div>
        </template>
        <template #cell-conclusion="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.conclusion }}</div>
          <input v-else class="tbl-input" v-model="row.conclusion" />
        </template>
        <template #cell-instructions="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.instructions }}</div>
          <input v-else class="tbl-input" v-model="row.instructions" />
        </template>
        <template #cell-deadline="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ formatIsoToDisplay(row.deadline) }}</div>
          <DatePicker v-else v-model="row.deadline" format="iso" />
        </template>
        <template #cell-is_completed="{ row }">
          <div v-if="!row.editing" class="cell-text">{{ row.is_completed ? $t('common.yes') : $t('common.no') }}</div>
          <input v-else type="checkbox" v-model="row.is_completed" />
        </template>
        <template #head-actions>
          <div class="tbl__header-text" style="justify-content: flex-end; text-align: right; width: 100%;"></div>
        </template>
        <template #cell-actions="{ row }">
          <div v-if="!readOnly" class="actions">
            <button v-if="!row.editing" class="actions__btn actions__btn--edit" @click="onEdit(row)" :title="$t('common.edit')" :aria-label="$t('common.edit')">
              <img class="actions__icon" src="../../assets/images/edit.svg" alt="edit" />
            </button>
            <template v-else>
              <button class="actions__btn actions__btn--accept" @click="onAccept(row)" :title="$t('common.save')" :aria-label="$t('common.save')">
                <img class="actions__icon" src="../../assets/images/accept.svg" alt="accept" />
              </button>
              <button class="actions__btn actions__btn--decline" @click="onDecline(row)" :title="$t('common.cancel')" :aria-label="$t('common.cancel')">
                <img class="actions__icon" src="../../assets/images/decline.svg" alt="decline" />
              </button>
            </template>
            <button class="actions__btn actions__btn--delete" @click="onDelete(row)" :title="$t('common.delete')" :aria-label="$t('common.delete')">
              <img class="actions__icon" src="../../assets/images/markdown/trash.svg" alt="delete" />
            </button>
          </div>
        </template>
      </DataTable>
      <div style="margin-top: 8px; display: flex; justify-content: flex-end; padding: 0 12px 12px;">
        <ActionButton v-if="!readOnly" :color="'#2A8A1B'" :text="$t('kps.inspections.addRecord')" @click="addRow" />
      </div>
    </div>

    <div class="page__footer">
      <ViewNavButtons
        :prev-text="$t('common.back')"
        :next-text="$t('common.next')"
        :prev-to="{ path: '/kps/officers-work' }"
        :next-to="{ path: '/kps/dd-members' }"
      />
    </div>
  </section>
</template>

<script>
import DataTable from '@/components/table/DataTable.vue'
import ViewNavButtons from '@/components/navigation/ViewNavButtons.vue'
import ActionButton from '@/components/widgets/ActionButton.vue'
import DatePicker from '@/components/widgets/DatePicker.vue'
import {checkOrCreateBookByDate, resolveBookIdForDate} from '@/api/book'
import { createInspection, updateInspection, deleteInspection } from '@/api/inspections'

import { ensureCurrentUser, getCachedCurrentUser } from '@/api/user'
import { canEdit } from '@/access/armAccess'

export default {
  name: 'KpsInspectionsView',
  components: { DataTable, ViewNavButtons, DatePicker , ActionButton },
  data() {
    return {
      currentUser: null,
      rows: [],
      bookId: null,
      bookContext: null,
      loading: false,
      error: null,
    }
  },
  computed: {
    canEditKps() {
      return canEdit(this.currentUser || getCachedCurrentUser(), '/kps/section-4')
    },
    readOnly() { return !this.canEditKps },
    columns() {
      return [
        { key: 'conclusion', label: this.$t('kps.inspections.colConclusion'), width: 360 },
        { key: 'instructions', label: this.$t('kps.inspections.colInstructions'), width: 260 },
        { key: 'deadline', label: this.$t('kps.inspections.colDeadline'), width: 170 },
        { key: 'is_completed', label: this.$t('kps.inspections.colCompleted'), width: 160 },
        { key: 'actions', label: '', width: 150 },
      ]
    },
    visibleColumns() {
      return this.readOnly ? this.columns.filter(c => c.key !== 'actions') : this.columns;
    },
  },
  async mounted() {
    try { this.currentUser = await ensureCurrentUser() }
    catch { this.currentUser = getCachedCurrentUser() }
    this.initAndFetch()
  },
  methods: {
    rowKey(r) { return r.id ?? r.__id },
    async initAndFetch() {
      try {
        this.loading = true
        const d = new Date()
        const two = (n) => (n < 10 ? `0${n}` : String(n))
        const dateISO = `${d.getFullYear()}-${two(d.getMonth()+1)}-${two(d.getDate())}`
        
        // Получаем полный контекст книги
        const bookCtx = await checkOrCreateBookByDate(dateISO)
        this.bookContext = bookCtx
        this.bookId = bookCtx?.id || bookCtx?.book?.id
        
        await this.fetchRows()
      } catch (e) {
        this.error = e?.message || String(e)
        console.error('Inspections: init/fetch failed', e)
      } finally {
        this.loading = false
      }
    },
    async ensureBookId() { return this.bookId },
    async fetchRows() {
      if (!this.bookId) return
      try {
        // Берем inspections из контекста
        const list = this.bookContext?.inspections || []
        
        if (Array.isArray(list)) {
          this.rows = list.map(it => ({
            id: it.id,
            conclusion: it.conclusion ?? '',
            ...this.splitDescription(it.description ?? ''),
            is_completed: it.is_completed ?? false,
            editing: false,
          }))
        }
      } catch (e) {
        console.error('Inspections: list failed', e)
      }
    },
    addRow() {
      const row = { __id: 'insp-' + Date.now(), conclusion: '', instructions: '', deadline: '', is_completed: false, editing: true }
      this.rows = [...this.rows, row]
    },
    onEdit(row) {
      row.__backup = JSON.parse(JSON.stringify(row))
      row.editing = true
    },
    async onAccept(row) {
      try {
        const { __id, __backup, editing, instructions, deadline, ...rest } = row

        const payload = {
          ...rest,
          description: this.composeDescription(instructions, deadline),
        }
        let resp
        if (row.id) {
          resp = await updateInspection(row.id, payload)
        } else {
          const bid = await this.ensureBookId()
          if (!bid) { alert(this.$t('kps.inspections.noBookId')); return }
          resp = await createInspection({ book: bid, ...payload })
          if (resp && resp.id) row.id = resp.id
        }
        row.editing = false
        delete row.__backup
      } catch (e) {
        console.error('Inspections: save failed', e)
        alert(this.$t('common.saveFailed') + ': ' + (e?.message || e))
      }
    },
    onDecline(row) {
      if (row.__backup) {
        const b = row.__backup
        Object.keys(b).forEach(k => { if (k !== '__backup') row[k] = b[k] })
        delete row.__backup
      }
      row.editing = false
    },
    async onDelete(row) {
      const id = row.__id ?? row.id
      if (!row.id) {
        const idx = this.rows.findIndex(r => (r.__id ?? r.id) === id)
        if (idx !== -1) this.rows = [...this.rows.slice(0, idx), ...this.rows.slice(idx + 1)]
        return
      }
      try {
        await deleteInspection(row.id)
        const idx = this.rows.findIndex(r => (r.__id ?? r.id) === id)
        if (idx !== -1) this.rows = [...this.rows.slice(0, idx), ...this.rows.slice(idx + 1)]
      } catch (e) {
        console.error('Inspections: delete failed', e)
        alert(this.$t('common.deleteFailed') + ': ' + (e?.message || e))
      }
    },

    // UI helpers
    formatIsoToDisplay(iso) {
      if (!iso) return ''
      if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
        const [y, m, d] = iso.split('-')
        return `${d}.${m}.${y}`
      }
      return iso
    },
    normalizeToIsoDate(value) {
      if (!value) return ''
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
      if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
        const [d, m, y] = value.split('.')
        return `${y}-${m}-${d}`
      }
      if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
        const [d, m, y] = value.split('-')
        return `${y}-${m}-${d}`
      }
      return ''
    },
    splitDescription(description) {
      const raw = String(description || '').trim()
      if (!raw) return { instructions: '', deadline: '' }

      // Try to parse common patterns:
      // 1) "...\nСроки исполнения: YYYY-MM-DD"
      // 2) "... || YYYY-MM-DD"
      if (raw.includes('||')) {
        const [left, right] = raw.split('||')
        return {
          instructions: (left || '').trim(),
          deadline: this.normalizeToIsoDate((right || '').trim()),
        }
      }

      const m = raw.match(/\n?\s*Срок(?:и\s*исполнения)?\s*:\s*(.+)\s*$/i)
      if (m) {
        const deadlineRaw = (m[1] || '').trim()
        const instructions = raw.replace(m[0], '').trim()
        return { instructions, deadline: this.normalizeToIsoDate(deadlineRaw) }
      }

      return { instructions: raw, deadline: '' }
    },
    composeDescription(instructions, deadlineIso) {
      const left = String(instructions || '').trim()
      const rightIso = this.normalizeToIsoDate(String(deadlineIso || '').trim())
      const right = rightIso ? `Сроки исполнения: ${this.formatIsoToDisplay(rightIso)}` : ''
      return [left, right].filter(Boolean).join('\n')
    },
  },
}
</script>

<style scoped>
.page {
  background: #FFFFFF;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.page__header { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.page__title { margin: 0; color: #111827; font-size: 24px; font-weight: 700; line-height: 31.2px; }

.section-card { position: relative; background: #FFFFFF; border-radius: 16px; padding: 0; }

.header-left {
  display: flex;
  width: 100%;
  justify-content: flex-start;
  color: #687588;
  font-size: 12px;
  font-weight: 700;
  line-height: 19.2px;
  letter-spacing: 0.2px;
}

.page__footer { display: flex; justify-content: flex-end; margin-top: auto; }

/* Минимальные стили для CRUD */
.cell-text { flex: 1 1 0; color: #111827; font-size: 16px; line-height: 20px; width: 100%; }
.tbl-input {
  width: 100%;
  height: 32px;
  padding: 6px 10px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: 16px;
  line-height: 20px;
  font-family: 'Times New Roman', Times, serif;
  color: #111827;
  background: #fff;
}
.tbl-input:focus { outline: 2px solid rgba(47,120,238,.25); outline-offset: 0; }

/* Prevent date value truncation in narrow cells (Chrome). */
.tbl-input[type="date"] {
  min-width: 150px;
}

.actions { display: inline-flex; align-items: center; gap: 10px; justify-content: flex-end; width: 100%; flex-wrap: wrap; }
.actions__btn { width: 30px; height: 30px; padding: 6px; border-radius: 8px; border: 0; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.actions__icon { width: 16px; height: 16px; display: block; }
.actions__btn--edit    { background: #2F78EE; }
.actions__btn--accept  { background: #2A8A1B; }
.actions__btn--decline { background: #D81717; }
.actions__btn--delete  { background: #D81717; }

</style>
